import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Radio as RadioIcon, Check, Save, Plus, Trash2, Pencil, X } from "lucide-react";
import { sr } from "../../lib/sr";

const DAYPARTS = ["morning", "midday", "afternoon", "evening", "weekend"];
const emptyStation = () => ({
  name: "", market: "Dallas–Fort Worth", format: "", target_market: "", description: "",
  coverage_area: "", rate_card: "", estimated_reach: "",
  daypart_listeners: { morning: "", midday: "", afternoon: "", evening: "", weekend: "" },
});

function StationCard({ station, onSaved }) {
  const seed = station.audience || station.model || {};
  const [form, setForm] = useState({
    morning: seed.morning ?? "", midday: seed.midday ?? "", afternoon: seed.afternoon ?? "",
    weekly_listeners: station.audience?.weekly_listeners ?? "", notes: station.audience?.notes ?? "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      const body = {
        morning: form.morning === "" ? null : Number(form.morning),
        midday: form.midday === "" ? null : Number(form.midday),
        afternoon: form.afternoon === "" ? null : Number(form.afternoon),
        weekly_listeners: form.weekly_listeners === "" ? null : Number(form.weekly_listeners),
        notes: form.notes,
      };
      const r = await sr.adminSetStationAudience(station.key, body);
      toast.success(r.modeled ? `${station.name} reach is now on` : `${station.name} saved`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="sr-card" style={{ marginBottom: 12 }} data-testid={`ra-station-${station.key}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div>
          <strong style={{ color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center" }}><RadioIcon size={15} /> {station.name}</strong>
          <span className="sr-muted" style={{ fontSize: 12 }}> · {station.delivery}</span>
        </div>
        {station.modeled
          ? <span className="sr-badge sr-badge-green" data-testid={`ra-status-${station.key}`}><Check size={12} /> {station.is_default_model ? "Default model" : "Reach on"}</span>
          : <span className="sr-badge sr-badge-amber" data-testid={`ra-status-${station.key}`}>Needs audience</span>}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10 }}>
        {["morning", "midday", "afternoon"].map((d) => (
          <div className="sr-field" key={d}>
            <label className="sr-label" style={{ textTransform: "capitalize" }}>{d} avg / spot</label>
            <input className="sr-input" type="number" min="0" value={form[d]} onChange={set(d)} placeholder="listeners" data-testid={`ra-${station.key}-${d}`} />
          </div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 10, marginTop: 8 }}>
        <div className="sr-field"><label className="sr-label">Weekly listeners (optional)</label>
          <input className="sr-input" type="number" min="0" value={form.weekly_listeners} onChange={set("weekly_listeners")} data-testid={`ra-${station.key}-weekly`} /></div>
        <div className="sr-field"><label className="sr-label">Notes (assumptions)</label>
          <input className="sr-input" value={form.notes} onChange={set("notes")} placeholder="e.g. middle-scenario penetration" data-testid={`ra-${station.key}-notes`} /></div>
      </div>
      <button className="sr-btn sr-btn-primary sr-btn-sm" style={{ marginTop: 10 }} onClick={save} disabled={busy} data-testid={`ra-save-${station.key}`}>
        {busy ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save audience</>}
      </button>
    </div>
  );
}

function CatalogStationForm({ initial, onCancel, onSave, saving }) {
  const [form, setForm] = useState(initial || emptyStation());
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setDp = (k) => (e) => setForm({ ...form, daypart_listeners: { ...form.daypart_listeners, [k]: e.target.value } });
  const _dp = form.daypart_listeners || {};
  const _num = (x) => (x === "" || x == null ? 0 : Number(x) || 0);
  const weeklyReach = 5 * (_num(_dp.morning) + _num(_dp.midday) + _num(_dp.afternoon) + _num(_dp.evening)) + 2 * _num(_dp.weekend);
  const submit = () => {
    if (!(form.name || "").trim()) { toast.error("Station name is required"); return; }
    onSave({
      name: form.name, market: form.market, format: form.format, target_market: form.target_market,
      description: form.description, coverage_area: form.coverage_area,
      rate_card: form.rate_card === "" ? 0 : Number(form.rate_card),
      estimated_reach: form.estimated_reach === "" ? 0 : Number(form.estimated_reach),
      daypart_listeners: Object.fromEntries(DAYPARTS.map((d) => [d, form.daypart_listeners?.[d] ?? ""])),
    });
  };
  return (
    <div className="sr-card" style={{ marginBottom: 12, border: "1px solid var(--blue)" }} data-testid="ra-catalog-form">
      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
        <div className="sr-field"><label className="sr-label">Station name *</label>
          <input className="sr-input" value={form.name} onChange={set("name")} placeholder="e.g. Radio Punjab 91.5" data-testid="ra-catalog-name" /></div>
        <div className="sr-field"><label className="sr-label">Market</label>
          <input className="sr-input" value={form.market} onChange={set("market")} data-testid="ra-catalog-market" /></div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 4 }}>
        <div className="sr-field"><label className="sr-label">Format / delivery</label>
          <input className="sr-input" value={form.format} onChange={set("format")} placeholder="e.g. 91.5 FM · Punjabi" data-testid="ra-catalog-format" /></div>
        <div className="sr-field"><label className="sr-label">Target market connection</label>
          <input className="sr-input" value={form.target_market} onChange={set("target_market")} placeholder="e.g. South Asian / Punjabi community" data-testid="ra-catalog-target" /></div>
      </div>
      <div className="sr-field" style={{ marginTop: 4 }}><label className="sr-label">Brief description</label>
        <input className="sr-input" value={form.description} onChange={set("description")} placeholder="What this station is known for" data-testid="ra-catalog-desc" /></div>
      <div style={{ marginTop: 8 }}>
        <label className="sr-label">Listener count by daypart (planning estimate)</label>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 8, marginTop: 4 }}>
          {DAYPARTS.map((d) => (
            <div className="sr-field" key={d}>
              <label className="sr-label" style={{ textTransform: "capitalize", fontSize: 11 }}>{d}</label>
              <input className="sr-input" type="number" min="0" value={form.daypart_listeners?.[d] ?? ""} onChange={setDp(d)} placeholder="listeners" data-testid={`ra-catalog-dp-${d}`} />
            </div>
          ))}
        </div>
        {weeklyReach > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8, background: "var(--blue-light)", border: "1px solid var(--blue)", borderRadius: 8, padding: "7px 11px" }} data-testid="ra-catalog-reach-preview">
            <span style={{ fontSize: 12, color: "var(--navy)" }}>Est. weekly reach ≈ <strong data-testid="ra-catalog-reach-value">{weeklyReach.toLocaleString()}</strong> listener-impressions <span className="sr-muted">· 5 weekdays + weekend, planning estimate</span></span>
            <button type="button" className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => setForm((f) => ({ ...f, estimated_reach: weeklyReach }))} data-testid="ra-catalog-reach-apply">Use as weekly reach</button>
          </div>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 8 }}>
        <div className="sr-field"><label className="sr-label">Est. weekly reach</label>
          <input className="sr-input" type="number" min="0" value={form.estimated_reach} onChange={set("estimated_reach")} data-testid="ra-catalog-reach" /></div>
        <div className="sr-field"><label className="sr-label">Rate card ($/spot)</label>
          <input className="sr-input" type="number" min="0" value={form.rate_card} onChange={set("rate_card")} data-testid="ra-catalog-rate" /></div>
        <div className="sr-field"><label className="sr-label">Coverage area</label>
          <input className="sr-input" value={form.coverage_area} onChange={set("coverage_area")} data-testid="ra-catalog-coverage" /></div>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={submit} disabled={saving} data-testid="ra-catalog-submit">
          {saving ? <Loader2 size={13} className="sr-spin" /> : <><Save size={13} /> Save station</>}
        </button>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={onCancel} data-testid="ra-catalog-cancel"><X size={13} /> Cancel</button>
      </div>
    </div>
  );
}

function CatalogStations() {
  const [data, setData] = useState(null);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => sr.adminCatalogStations().then(setData).catch(() => setData({ stations: [] }));
  useEffect(() => { load(); }, []);

  const create = async (body) => {
    setSaving(true);
    try { await sr.adminCreateCatalogStation(body); toast.success("Station added — available during radio booking"); setAdding(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add station"); } finally { setSaving(false); }
  };
  const update = async (id, body) => {
    setSaving(true);
    try { await sr.adminUpdateCatalogStation(id, body); toast.success("Station updated"); setEditing(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); } finally { setSaving(false); }
  };
  const remove = async (s) => {
    if (!window.confirm(`Remove "${s.name}" from the booking catalog?`)) return;
    try { await sr.adminDeleteCatalogStation(s.id); toast.success("Station removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove"); }
  };

  const editSeed = (s) => ({
    name: s.name || "", market: s.market || "", format: s.format || "", target_market: s.target_market || "",
    description: s.description || "", coverage_area: s.coverage_area || "",
    rate_card: s.rate_card ?? "", estimated_reach: s.estimated_reach ?? "",
    daypart_listeners: { ...emptyStation().daypart_listeners, ...(s.daypart_listeners || {}) },
  });

  const stations = data?.stations || [];
  return (
    <div style={{ marginTop: 28 }} data-testid="ra-catalog-section">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h2 style={{ color: "var(--navy)", fontSize: 20, margin: 0 }}>Additional radio stations</h2>
          <p className="sr-muted" style={{ fontSize: 13, margin: "2px 0 0" }}>{data?.note || "Add stations that become selectable during radio campaign booking."}</p>
        </div>
        {!adding && !editing && (
          <button className="sr-btn sr-btn-primary sr-btn-sm" onClick={() => setAdding(true)} data-testid="ra-catalog-add"><Plus size={14} /> Add station</button>
        )}
      </div>

      {adding && <div style={{ marginTop: 12 }}><CatalogStationForm onCancel={() => setAdding(false)} onSave={create} saving={saving} /></div>}

      <div style={{ marginTop: 12 }}>
        {data === null ? (
          <div style={{ padding: 24, textAlign: "center" }}><Loader2 className="sr-spin" /></div>
        ) : stations.length === 0 && !adding ? (
          <div className="sr-card sr-empty" data-testid="ra-catalog-empty" style={{ textAlign: "center", padding: 28 }}>
            <RadioIcon size={26} color="var(--blue)" />
            <p style={{ marginTop: 8, fontSize: 14 }}>No additional stations yet. Add one to make it bookable in radio campaigns.</p>
          </div>
        ) : stations.map((s) => (
          editing === s.id ? (
            <CatalogStationForm key={s.id} initial={editSeed(s)} onCancel={() => setEditing(null)} onSave={(body) => update(s.id, body)} saving={saving} />
          ) : (
            <div className="sr-card" style={{ marginBottom: 10 }} key={s.id} data-testid={`ra-catalog-${s.id}`}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ color: "var(--navy)", display: "inline-flex", gap: 6, alignItems: "center" }}><RadioIcon size={15} /> {s.name}</strong>
                  <div className="sr-muted" style={{ fontSize: 12, marginTop: 2 }}>
                    {[s.format, s.market].filter(Boolean).join(" · ")}{s.target_market ? ` · ${s.target_market}` : ""}
                  </div>
                  {s.description && <div className="sr-muted" style={{ fontSize: 12, marginTop: 4 }}>{s.description}</div>}
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
                    {DAYPARTS.map((d) => (
                      <span key={d} className="sr-badge sr-badge-gray" style={{ textTransform: "capitalize", fontSize: 11 }}>
                        {d}: {s.daypart_listeners?.[d] != null ? Number(s.daypart_listeners[d]).toLocaleString() : "—"}
                      </span>
                    ))}
                  </div>
                  <div className="sr-muted" style={{ fontSize: 11, marginTop: 6 }}>
                    {s.estimated_reach ? `~${Number(s.estimated_reach).toLocaleString()} weekly reach` : "reach est. not set"}
                    {s.rate_card ? ` · $${Number(s.rate_card).toLocaleString()}/spot` : ""}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => { setAdding(false); setEditing(s.id); }} data-testid={`ra-catalog-edit-${s.id}`}><Pencil size={13} /></button>
                  <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => remove(s)} data-testid={`ra-catalog-del-${s.id}`}><Trash2 size={13} /></button>
                </div>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

export default function RadioAudiences() {
  const [data, setData] = useState(null);
  const load = () => sr.adminRadioStations().then(setData).catch((e) => { toast.error(e?.response?.data?.detail || "Staff only"); setData({ stations: [] }); });
  useEffect(() => { load(); }, []);
  if (!data) return <div className="sr-empty" style={{ padding: 60, textAlign: "center" }}><Loader2 className="sr-spin" /></div>;
  return (
    <div style={{ maxWidth: 860, margin: "0 auto" }} data-testid="radio-audiences-page">
      <h1 style={{ color: "var(--navy)", fontSize: 26, marginBottom: 4 }}>Radio stations</h1>
      <p className="sr-muted" style={{ marginBottom: 16 }}>Manage the FunAsia network's audience inputs and add extra stations to the booking catalog. All listener numbers are planning estimates, not measured ratings.</p>
      <h2 style={{ color: "var(--navy)", fontSize: 20, marginBottom: 2 }}>FunAsia network — station audience inputs</h2>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 12 }}>{data.note}</p>
      {(data.stations || []).map((s) => <StationCard key={s.key} station={s} onSaved={load} />)}
      <CatalogStations />
    </div>
  );
}
